import { useId, type CSSProperties, type ReactNode } from "react";
import {
  BlockBackgroundMediaPreview,
  isBackgroundVideoAttachment,
} from "@/components/cms-puck/previews/BlockBackgroundMediaPreview";
import { usePuckTheme } from "@/context/cms-puck/PuckThemeContext";
import {
  blockStyleNeedsCssRules,
  blockEntranceClass,
  BLOCK_ENTRANCE_CSS,
  buildBlockCssRules,
  normalizeBlockStyle,
  resolveBlockContentMaxWidth,
  resolveBlockStyleCss,
  type BlockStyle,
} from "@/lib/cms-puck/block-style";
import {
  blockContentShellClass,
  type LayoutMaxWidth,
} from "@/lib/cms-puck/layout";
import { useGetAttachmentById, type Attachment } from "@/api/attachment";

/**
 * StyleWrapper (APERÇU BO) — KEEP IN SYNC avec
 * inventozen-frontpublic/src/cms-puck/StyleWrapper.tsx.
 */
export function StyleWrapper({
  style,
  className: extraClassName,
  contentMaxWidthFallback,
  children,
}: {
  style: BlockStyle | undefined;
  className?: string;
  contentMaxWidthFallback?: LayoutMaxWidth | null;
  children: ReactNode;
}) {
  const theme = usePuckTheme();
  const palette = theme?.colors ?? [];
  const normalized = normalizeBlockStyle(style);
  const attachmentId = normalized.design.backgroundImage.attachmentId ?? "";
  const { data } = useGetAttachmentById(attachmentId);
  const attachment = attachmentId ? (data as Attachment | undefined) : undefined;
  const renderBackgroundAsMedia = isBackgroundVideoAttachment(attachment);
  const backgroundImageUrl =
    attachment && !renderBackgroundAsMedia
      ? attachment.largePublicUrl ??
        attachment.mediumPublicUrl ??
        attachment.publicUrl
      : undefined;
  const css = resolveBlockStyleCss(palette, normalized, {
    backgroundImageUrl,
    renderBackgroundAsMedia,
  }) as CSSProperties;
  if (renderBackgroundAsMedia) {
    css.position = "relative";
  }
  const uid = useId();
  const needsRules = blockStyleNeedsCssRules(normalized);
  const blockClass = `cms-blk-${uid.replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const rules = needsRules
    ? buildBlockCssRules(`.${blockClass}`, normalized)
    : null;
  const attrs = normalized.attributes;
  const entranceClass = blockEntranceClass(normalized.effects.entrance);
  const className =
    [needsRules ? blockClass : null, entranceClass, extraClassName, attrs.cssClass]
      .filter(Boolean)
      .join(" ") || undefined;
  const contentShellClass = blockContentShellClass(
    resolveBlockContentMaxWidth(style, contentMaxWidthFallback),
  );
  return (
    <div id={attrs.cssId ?? undefined} className={className} style={css}>
      {renderBackgroundAsMedia && attachment ? (
        <BlockBackgroundMediaPreview
          attachment={attachment}
          fit={normalized.design.backgroundImage.fit}
          overlay={normalized.design.backgroundImage.overlay}
        />
      ) : null}
      {entranceClass ? <style>{BLOCK_ENTRANCE_CSS}</style> : null}
      {rules ? <style>{rules}</style> : null}
      {contentShellClass ? (
        <div
          className={contentShellClass}
          style={renderBackgroundAsMedia ? { position: "relative", zIndex: 2 } : undefined}
        >
          {children}
        </div>
      ) : renderBackgroundAsMedia ? (
        <div style={{ position: "relative", zIndex: 2 }}>{children}</div>
      ) : (
        children
      )}
    </div>
  );
}
